import { isFeatureEnabled } from '@src/config/featureFlags';
import { getAccessToken, getExpiresAt, getRefreshToken } from '@src/modules/security/authHelpers';
// Modules
import store from '@src/store/index';
import initializeState from '@src/store/initialization/initialize_state';
import setExampleData from '@src/store/initialization/set_examble_data';
import { type AuthUserState, clearAuth, setAuth } from '@src/store/slices/auth';
import { updateBootProcessComplete } from '@src/store/slices/boot_process';
import { updateFocusMode } from '@src/store/slices/focus_mode';
import { useEffect, useRef } from 'react';
import { getReduxStoreFromLocalStorage } from '../../modules/local_storage_utils';

// localStorageからユーザー情報を復元
function loadUserFromStorage(): AuthUserState | null {
  try {
    const userJson = localStorage.getItem('auth_user');
    if (userJson) {
      return JSON.parse(userJson);
    }
  } catch (error) {
    console.error('Failed to load user from localStorage:', error);
  }
  return null;
}

// トークン情報を復元
function loadTokenInfo(): { accessToken: string; refreshToken: string; expiresAt: string } | null {
  try {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    const expiresAt = getExpiresAt();

    if (accessToken && refreshToken && expiresAt) {
      return { accessToken, refreshToken, expiresAt };
    }
  } catch (error) {
    console.error('Failed to load token info from localStorage:', error);
  }
  return null;
}

// 認証状態を復元
function restoreAuthState(): void {
  const user = loadUserFromStorage();
  const tokenInfo = loadTokenInfo();

  // ユーザー情報とトークンの両方が存在する場合のみ認証状態を復元
  if (user && tokenInfo) {
    // トークンの有効期限チェック
    const expiresAt = new Date(tokenInfo.expiresAt);
    const now = new Date();

    if (expiresAt > now) {
      // 有効期限内の場合は認証状態を復元
      store.dispatch(
        setAuth({
          user,
          accessToken: tokenInfo.accessToken,
          refreshToken: tokenInfo.refreshToken,
          expiresAt: tokenInfo.expiresAt,
        })
      );
    } else {
      // 有効期限切れの場合は認証情報をクリア
      store.dispatch(clearAuth());
    }
  } else if (user || tokenInfo) {
    // どちらか一方だけ存在する場合は、不整合なので全てクリア
    store.dispatch(clearAuth());
  }
}

// 起動シーケンス制御のカスタムフック
export default function useBootsrap() {
  const hasRestoredAuth = useRef(false);

  useEffect(() => {
    const initializeApp = async () => {
      // 【最優先】認証状態を復元（初回のみ）
      if (!hasRestoredAuth.current) {
        restoreAuthState();
        hasRestoredAuth.current = true;
      }

      // 認証機能が無効化されている場合は、常にローカルストレージを使用
      if (!isFeatureEnabled('AUTH_ENABLED')) {
        const s = getReduxStoreFromLocalStorage();
        if (s === null) {
          // フォーカスモードのデフォルト初期化（通常モード）
          store.dispatch(updateFocusMode(false));
          setExampleData();
        } else {
          // ローカルストレージからの復帰処理を先に実行
          initializeState(s);
        }

        // 初期化完了を即座に通知（遅延なし）
        store.dispatch(updateBootProcessComplete());
        return;
      }

      // 認証済み・未認証に関わらず、ローカルストレージからデータを読み込み
      const s = getReduxStoreFromLocalStorage();
      if (s === null) {
        // フォーカスモードのデフォルト初期化（通常モード）
        store.dispatch(updateFocusMode(false));
        setExampleData();
      } else {
        // ローカルストレージからの復帰処理を先に実行
        initializeState(s);
      }

      // 初期化完了を即座に通知（遅延なし）
      store.dispatch(updateBootProcessComplete());
    };

    initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
